/**
 * FikoNesting Dialog — 2D Levha Yerleşim Optimizasyonu UI
 * Parça listesi + levha → MaxRects yerleşim → SVG görsel + fire/verim. Modelden footprint alabilir.
 */
import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { nestingHesapla, NestParca, NestSonuc, NestYerlesim } from './FikoNesting';

@Component({
  selector: 'fikoyt-nesting',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
  <div class="ns">
    <div class="ns-levha">
      <div class="ns-in"><label>Levha En</label><input type="number" [(ngModel)]="levhaEn" min="10"></div>
      <div class="ns-in"><label>Levha Boy</label><input type="number" [(ngModel)]="levhaBoy" min="10"></div>
      <div class="ns-in"><label>Pay (kerf)</label><input type="number" [(ngModel)]="bosluk" min="0"></div>
    </div>

    <div class="ns-baslik">Parçalar <button class="ns-mini" (click)="modeldenAl()" title="Aktif modelin taban ölçüsünü ekle">＋ modelden</button></div>
    <div class="ns-tablo">
      <div class="ns-th"><span>Ad</span><span>En</span><span>Boy</span><span>Adet</span><span>↻</span><span></span></div>
      @for (p of parcalar; track $index) {
        <div class="ns-tr">
          <input [(ngModel)]="p.ad" class="ad">
          <input type="number" [(ngModel)]="p.en" min="1">
          <input type="number" [(ngModel)]="p.boy" min="1">
          <input type="number" [(ngModel)]="p.adet" min="1">
          <input type="checkbox" [(ngModel)]="p.donebilir" title="Döndürülebilir">
          <button class="ns-sil" (click)="parcaSil($index)">✕</button>
        </div>
      }
    </div>
    <div class="ns-ekle"><button class="ns-mini" (click)="parcaEkle()">＋ Parça Ekle</button></div>
    <button class="ns-btn primary" (click)="yerlestir()">📐 Yerleştir & Optimize Et</button>

    @if (sonuc) {
      <div class="ns-ozet">
        <div class="ns-r"><span>Levha sayısı</span><b>{{ sonuc.levhaSayisi }}</b></div>
        <div class="ns-r"><span>Yerleşen / Yerleşemeyen</span><b>{{ sonuc.yerlesen }} / {{ sonuc.yerlesemeyen }}</b></div>
        <div class="ns-r vurgu"><span>Verim (kullanım)</span><b>%{{ sonuc.verimYuzde.toFixed(1) }}</b></div>
        <div class="ns-r fire"><span>Fire</span><b>%{{ sonuc.fireYuzde.toFixed(1) }}</b></div>
        @if (sonuc.yerlesemeyenler.length) { <div class="ns-uyari">Sığmayan: {{ sonuc.yerlesemeyenler.join(', ') }}</div> }
      </div>
      <div class="ns-gorsel">
        @for (li of levhaIdxleri; track li) {
          <div class="ns-sayfa">
            <div class="ns-sayfa-baslik">Levha {{ li + 1 }}</div>
            <svg [attr.viewBox]="'0 0 ' + levhaEn + ' ' + levhaBoy" preserveAspectRatio="xMidYMid meet" class="ns-svg">
              <rect x="0" y="0" [attr.width]="levhaEn" [attr.height]="levhaBoy" fill="#0a1f2e" stroke="#2b8fb0" stroke-width="2"/>
              @for (y of levhaYerlesimleri(li); track $index) {
                <g>
                  <rect [attr.x]="y.x" [attr.y]="y.y" [attr.width]="y.en" [attr.height]="y.boy"
                        [attr.fill]="renk(y.ad)" fill-opacity="0.75" stroke="#fff" stroke-width="1.2"/>
                  <text [attr.x]="y.x + y.en/2" [attr.y]="y.y + y.boy/2" fill="#fff" [attr.font-size]="etiketBoyut(y)" text-anchor="middle" dominant-baseline="middle">{{ y.ad }}{{ y.dondu ? ' ↻' : '' }}</text>
                </g>
              }
            </svg>
          </div>
        }
      </div>
    }
    @if (hata) { <div class="ns-hata">{{ hata }}</div> }
  </div>
  `,
  styles: [`
    .ns { padding:10px; min-width:320px; max-width:380px; font-size:11px; color:#e6f6ff;
      background:linear-gradient(180deg,rgba(0,40,60,.96),rgba(0,18,36,.98)); border-radius:6px; }
    .ns-levha { display:grid; grid-template-columns:1fr 1fr 1fr; gap:6px; margin-bottom:8px; }
    .ns-in { display:flex; flex-direction:column; gap:2px; }
    .ns-in label { font-size:9px; color:#5fd9f5; }
    .ns-in input, .ns-tr input, .ns-th { background:rgba(0,60,80,.8); border:1px solid rgba(0,200,220,.35); color:#fff; padding:4px; border-radius:4px; font-size:10px; outline:none; box-sizing:border-box; }
    .ns-baslik { display:flex; justify-content:space-between; align-items:center; color:#5fd9f5; font-weight:600; margin:4px 0; }
    .ns-mini { background:rgba(0,90,110,.6); border:1px solid rgba(0,200,220,.4); color:#bdf; border-radius:4px; padding:2px 8px; font-size:10px; cursor:pointer; }
    .ns-mini:hover { background:rgba(0,130,160,.8); }
    .ns-tablo { display:flex; flex-direction:column; gap:3px; }
    .ns-th, .ns-tr { display:grid; grid-template-columns:1fr 48px 48px 40px 22px 22px; gap:3px; align-items:center; }
    .ns-th { background:none; border:none; color:#7fc6dd; font-size:9px; padding:0 2px; }
    .ns-tr input.ad { text-align:left; }
    .ns-tr input[type=number] { text-align:center; }
    .ns-sil { background:rgba(120,40,40,.6); border:1px solid rgba(255,100,100,.4); color:#ff9a9a; border-radius:4px; cursor:pointer; padding:3px 0; }
    .ns-ekle { margin:6px 0; }
    .ns-btn { width:100%; padding:8px; border:none; border-radius:5px; font-weight:600; cursor:pointer; color:#fff; }
    .ns-btn.primary { background:linear-gradient(90deg,#0a84c0,#0bb39e); }
    .ns-ozet { margin-top:10px; background:rgba(0,0,0,.35); border:1px solid rgba(0,200,220,.3); border-radius:5px; padding:8px; }
    .ns-r { display:flex; justify-content:space-between; padding:3px 0; border-bottom:1px solid rgba(0,200,220,.12); }
    .ns-r span { color:#9fe8f7; }
    .ns-r.vurgu b { color:#7dffc0; font-size:14px; } .ns-r.fire b { color:#ffb36a; }
    .ns-uyari { margin-top:4px; color:#ffb36a; font-size:10px; }
    .ns-gorsel { margin-top:8px; display:flex; flex-direction:column; gap:8px; }
    .ns-sayfa-baslik { font-size:10px; color:#5fd9f5; margin-bottom:2px; }
    .ns-svg { width:100%; max-height:240px; background:#06141e; border-radius:4px; }
    .ns-hata { margin-top:8px; padding:6px; background:rgba(255,50,50,.2); border:1px solid rgba(255,100,100,.4); border-radius:4px; color:#ff9a9a; text-align:center; }
  `]
})
export class FikoNestingComponent {
  @Input() anaGosterim: any = null;
  @Input() pencere: any;
  @Input() ayarlar: any = {};
  @Input() modelleme: any = null;

  levhaEn = 2000; levhaBoy = 1000; bosluk = 5;
  parcalar: NestParca[] = [
    { ad: 'P1', en: 400, boy: 300, adet: 6, donebilir: true },
    { ad: 'P2', en: 250, boy: 250, adet: 4, donebilir: true },
    { ad: 'P3', en: 600, boy: 180, adet: 3, donebilir: true },
  ];
  sonuc: NestSonuc | null = null;
  hata: string | null = null;
  private renkCache = new Map<string, string>();

  constructor(private cd: ChangeDetectorRef) {}

  get levhaIdxleri(): number[] { return this.sonuc ? Array.from({ length: this.sonuc.levhaSayisi }, (_, i) => i) : []; }
  levhaYerlesimleri(li: number): NestYerlesim[] { return this.sonuc ? this.sonuc.yerlesimler.filter(y => y.levha === li) : []; }

  parcaEkle(): void { this.parcalar.push({ ad: 'P' + (this.parcalar.length + 1), en: 300, boy: 200, adet: 1, donebilir: true }); }
  parcaSil(i: number): void { this.parcalar.splice(i, 1); }

  /** Aktif 3D modelin taban ölçüsünü (bbox X×Y) parça olarak ekler. */
  modeldenAl(): void {
    const mesh = this.modelleme?.govdeMesh || this.anaGosterim?.modelleme?.govdeMesh;
    if (!mesh) { this.hata = 'Aktif model yok'; this.cd.detectChanges(); return; }
    const b = new THREE.Box3().setFromObject(mesh); const s = new THREE.Vector3(); b.getSize(s);
    this.parcalar.push({ ad: 'Model', en: Math.round(s.x), boy: Math.round(s.y), adet: 1, donebilir: true });
    this.hata = null; this.cd.detectChanges();
  }

  yerlestir(): void {
    this.hata = null;
    if (!this.parcalar.length) { this.hata = 'En az bir parça ekleyin'; this.cd.detectChanges(); return; }
    try {
      this.sonuc = nestingHesapla(this.parcalar, { en: this.levhaEn, boy: this.levhaBoy, bosluk: this.bosluk });
      if (!this.sonuc.yerlesen) this.hata = 'Hiçbir parça yerleşmedi (levha ölçüsünü kontrol edin)';
    } catch (e: any) { this.hata = e.message || 'Yerleşim hatası'; }
    this.cd.detectChanges();
  }

  renk(ad: string): string {
    if (!this.renkCache.has(ad)) {
      let h = 0; for (let i = 0; i < ad.length; i++) h = (h * 31 + ad.charCodeAt(i)) % 360;
      this.renkCache.set(ad, `hsl(${h},65%,52%)`);
    }
    return this.renkCache.get(ad)!;
  }
  etiketBoyut(y: NestYerlesim): number { return Math.max(10, Math.min(y.en, y.boy) * 0.25); }
}
